'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';

const testimonials = [
  {
    key: 'anannya',
    name: 'Anannya Chakraborty',
    role: 'Senior Product Marketing Manager',
    quote: 'The frameworks and the capstone project were immensely and immediately useful, giving me a strong foundation to chart my own product marketing journey. Don\u2019t hesitate, it\u2019s worth it.',
    photo: '/assets/images/Anannya-1770119512958.jpeg',
  },
  {
    key: 'omkar',
    name: 'Omkar Yederi',
    role: 'Product Marketing Manager',
    quote: 'Beyond the materials and the experience, the accessibility and the mentorship are what make it special.',
    photo: '/assets/images/1748420429875-1783310035595.jpeg',
  },
  {
    key: 'sahar',
    name: 'Sahar Ashraf',
    role: 'Product Marketing Manager',
    quote: 'The course stood out for being immediately useful. I could immediately use the learnings at work.',
    photo: '/assets/images/Sahar-1770121183326.jpeg',
  },
];

const faqData = [
  { q: "What is Cohort 3?", a: "A 60-day cohort-based course from GoPMM, India's largest Product Marketing community. It combines six self-paced PMM sessions, AI-powered workflows, a real-world team capstone, and career support, built to be everything you need to succeed in Product Marketing." },
  { q: "Who is it for?", a: "Marketers transitioning into PMM, early-stage PMMs building their foundation, and working PMMs who want sharper craft and better leverage. The past two cohorts have been a mix of all three, and the capstone teams are matched to your career interests." },
  { q: "How does the week actually work?", a: "Every Tuesday a new session drops: videos, exercises, frameworks, and that session's AI workflow. You work through it at your own pace over the week. The following Tuesday, you bring your completed work to a live accountability session with mentors and peers, and the next session unlocks. Thursdays run expert and career sessions alongside." },
  { q: "Is this just recorded videos?", a: "No. Each session is a working system: video broken into module-length sections, a short exercise after each module to confirm the learning, a framework to complete, and an AI workflow that builds the finished output with you. Then you defend that work live, every week. The recording is what makes it flexible, the live loop is what makes it stick." },
  { q: "What is the AI component?", a: "Two sets of systems. Every session ships with an execution workflow: plug it into the LLM you already use and it does the mechanical assembly of that session's framework with you. Alongside, you get career workflows: resume-to-offer, a portfolio builder, and interview prep built on real PMM interview banks. All of them are yours after the course ends." },
  { q: "How much time should I plan for?", a: "5–7 hours a week, entirely on your own schedule. There are no fixed class hours, only the live Tuesday session to show up for." },
  { q: "What will I build?", a: "Completed frameworks from all six sessions, and a 2.5-week team capstone solving a live business problem from a real company, presented at Demo Day to a panel of industry leaders. Past cohorts worked on problems from Swiggy Instamart, Freshworks, Rocketlane, and Groww." },
  { q: "Is this only for people looking for a PMM job?", a: "No. If you are switching in, the capstone and career layer are built for you. If you are already in the role, the frameworks and AI workflows are built to make you measurably better at it. Two of our first five Cohort 3 members joined to grow in their current roles." },
  { q: "What happens after I apply?", a: "You fill in a short interest form, and we get on a call, not a sales pitch, a fit conversation. We walk through where you are, where you want to go, and whether Cohort 3 is the right vehicle. If it is, we handle enrolment and payment there." },
  { q: "Do I keep access after the cohort ends?", a: "Yes. The course materials, the AI workflows, the GoPMM community, and the Talent Hub, all of it stays with you." },
  { q: "Do you offer flexible payment options?", a: "Yes. We finalise what works for you during enrolment." },
];

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div
      className="cohort-faq-outer"
      style={{
        width: '100%',
        maxWidth: '1440px',
        margin: '0 auto',
        background: '#fff',
        padding: '100px 72px',
      }}
    >
      <h2
        style={{
          font: "800 44px/1.1 'Manrope', sans-serif",
          color: '#161B32',
          textAlign: 'center',
          margin: '0 0 64px',
        }}
      >
        Frequently Asked Questions
      </h2>
      <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
        {faqData?.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={i} style={{ borderBottom: '1px solid #ECEAF3' }}>
              <button
                onClick={() => setOpenIndex(isOpen ? null : i)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '22px 0',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  gap: '16px',
                }}
              >
                <span
                  style={{
                    font: "800 16px/1.4 'Manrope', sans-serif",
                    color: '#161B32',
                  }}
                >
                  {item?.q}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '1.5px solid rgba(22,27,50,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(22,27,50,0.4)',
                    fontSize: '16px',
                    lineHeight: '1',
                  }}
                >
                  {isOpen ? '×' : '+'}
                </span>
              </button>
              {isOpen && (
                <div
                  style={{
                    padding: '0 0 20px',
                    font: "500 15px/1.7 'Manrope', sans-serif",
                    color: 'rgba(22,27,50,0.65)',
                    maxWidth: '680px',
                  }}
                >
                  {item?.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CohortPage() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    document.title = "GoPMM Cohort 3 | Everything you need to succeed in Product Marketing";
  }, []);

  const active = testimonials?.[activeTestimonial];

  return (
    <div className="min-h-screen bg-white text-[#161B32] font-manrope overflow-x-hidden">
      <Header />
      {/* 1. Hero Section */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          height: '900px',
          background: '#fff',
          overflow: 'hidden',
        }}
        className="cohort-hero-outer"
      >
        {/* Background blobs */}
        <div
          style={{
            position: 'absolute',
            right: '-160px',
            top: '-200px',
            width: '740px',
            height: '740px',
            borderRadius: '50%',
            background:
              'conic-gradient(from 190deg, #5D1AFA 0%, #8D42FA 28%, #FE4DE1 56%, #FC9D5C 78%, #5D1AFA 100%)',
            filter: 'blur(88px)',
            opacity: 0.36,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: '80px',
            bottom: '-80px',
            width: '320px',
            height: '320px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FE4DE1, #FC9D5C)',
            filter: 'blur(72px)',
            opacity: 0.2,
            pointerEvents: 'none',
          }}
        />

        {/* Ribbon banner */}
        <div
          style={{
            position: 'absolute',
            top: '22px',
            right: '-52px',
            background: '#161B32',
            color: '#fff',
            font: "800 10px/1 'Manrope', sans-serif",
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '6px 56px',
            transform: 'rotate(45deg)',
            zIndex: 20,
          }}
          className="cohort-ribbon"
        >
          Starts Aug 18
        </div>

        {/* Spacer for fixed header */}
        <div style={{ height: '80px' }} />

        {/* Hero content */}
        <div
          style={{
            position: 'relative',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            height: 'calc(900px - 94px)',
            padding: '0 72px',
          }}
          className="cohort-hero-inner"
        >
          {/* Left column */}
          <div style={{ flex: '0 0 auto', width: '640px' }}>
            <div
              style={{
                display: 'inline-flex',
                background: '#F6F2FF',
                borderRadius: '999px',
                padding: '8px 18px',
                marginBottom: '30px',
              }}
            >
              <span
                style={{
                  font: "800 11px/1 'Manrope', sans-serif",
                  letterSpacing: '0.13em',
                  textTransform: 'uppercase',
                  background:
                    'linear-gradient(90deg, #5D1AFA 0%, #8D42FA 33%, #FE4DE1 66%, #FC9D5C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                COHORT 3 · STARTS AUGUST 18
              </span>
            </div>

            <h1
              style={{
                font: "800 60px/1.08 'Manrope', sans-serif",
                color: '#161B32',
                maxWidth: '600px',
                margin: '0 0 22px',
              }}
              className="cohort-h1"
            >
              Everything you need to succeed in{' '}
              <span
                style={{
                  background:
                    'linear-gradient(90deg, #5D1AFA 0%, #8D42FA 33%, #FE4DE1 66%, #FC9D5C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Product Marketing
              </span>
              .
            </h1>

            <p
              style={{
                font: "500 16.5px/1.68 'Manrope', sans-serif",
                color: 'rgba(22,27,50,0.65)',
                maxWidth: '560px',
                margin: '0 0 34px',
              }}
            >
              A 60-day cohort-based course from India&apos;s largest product marketing community,
              combining self-paced PMM learning, AI-powered workflows, a real-world capstone, and
              career support that continues after the course ends.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '13px', marginBottom: '42px' }}>
              {[
                '6 weeks of PMM foundational learning, self-paced, with live weekly support',
                'AI-powered systems to execute the work and accelerate your career',
                'A team capstone built on a real business problem, presented at Demo Day',
              ]?.map((text, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '11px' }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <defs>
                      <linearGradient id={`g${i + 1}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#5D1AFA" />
                        <stop offset="100%" stopColor="#FC9D5C" />
                      </linearGradient>
                    </defs>
                    <circle cx="9" cy="9" r="9" fill={`url(#g${i + 1})`} />
                    <polyline points="5,9.5 7.5,12 13,6.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  <span style={{ font: "500 15px/1.5 'Manrope', sans-serif", color: '#161B32' }}>{text}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Link
                href="https://forms.gle/FRbwdLgRbGjdgMNs5"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  font: "800 15px/1 'Manrope', sans-serif",
                  color: '#fff',
                  background:
                    'linear-gradient(135deg, #5D1AFA 0%, #8D42FA 42%, #FE4DE1 72%, #FC9D5C 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '16px 34px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                Apply for Cohort 3
              </Link>
              <span style={{ font: "600 14px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.50)' }}>
                Starts August 18
              </span>
            </div>
          </div>

          {/* Right column — floating cards */}
          <div style={{ flex: 1, position: 'relative', height: '100%' }} className="cohort-cards-col">
            {/* Card 1 — Alumni work at */}
            <div
              style={{
                position: 'absolute',
                top: '108px',
                left: '480px',
                width: '238px',
                background: '#fff',
                borderRadius: '18px',
                padding: '18px 20px 16px',
                boxShadow: '0 12px 40px rgba(22,27,50,0.11)',
                transform: 'rotate(-3deg)',
              }}
              className="cohort-card"
            >
              <p
                style={{
                  font: "700 10px/1 'Manrope', sans-serif",
                  color: 'rgba(22,27,50,0.4)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: '13px',
                }}
              >
                Alumni work at
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <img src="/assets/images/Google_Logo_1-1783306732898.png" alt="Google" style={{ height: '16px', width: 'auto', objectFit: 'contain', opacity: 0.85 }} />
                <img src="/assets/images/Sprinklr_id4MCRF60m_1-1783306944076.png" alt="Sprinklr" style={{ height: '16px', width: 'auto', objectFit: 'contain', opacity: 0.85 }} />
                <img src="/assets/images/idNBebdpKI_logos-1783307335246.png" alt="Pocket FM" style={{ height: '16px', width: 'auto', objectFit: 'contain' }} />
              </div>
            </div>

            {/* Card 2 — Now learning */}
            <div
              style={{
                position: 'absolute',
                top: '258px',
                left: '330px',
                width: '228px',
                background: '#fff',
                borderRadius: '18px',
                padding: '16px 18px 15px',
                boxShadow: '0 12px 40px rgba(22,27,50,0.11)',
                transform: 'rotate(3.5deg)',
              }}
              className="cohort-card"
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '11px' }}>
                <div>
                  <p style={{ font: "700 10px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.4)', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Now learning
                  </p>
                  <p style={{ font: "800 14px/1.2 'Manrope', sans-serif", color: '#161B32' }}>Session 3 of 6</p>
                </div>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #5D1AFA, #FE4DE1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '12px',
                  }}
                >
                  ▶
                </div>
              </div>
              <div style={{ height: '5px', background: '#F6F2FF', borderRadius: '999px', overflow: 'hidden', marginBottom: '7px' }}>
                <div style={{ height: '100%', width: '44%', background: 'linear-gradient(90deg, #5D1AFA, #FE4DE1)', borderRadius: '999px' }} />
              </div>
              <p style={{ font: "500 11px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.36)' }}>44% complete</p>
            </div>

            {/* Card 3 — AI Workflows */}
            <div
              style={{
                position: 'absolute',
                top: '402px',
                left: '178px',
                width: '222px',
                background: '#fff',
                borderRadius: '18px',
                padding: '16px 18px 15px',
                boxShadow: '0 12px 40px rgba(22,27,50,0.11)',
              }}
              className="cohort-card"
            >
              <p style={{ font: "700 10px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.4)', textTransform: 'uppercase', marginBottom: '9px' }}>
                AI Workflows ★
              </p>
              <p
                style={{
                  font: "800 22px/1 'Manrope', sans-serif",
                  background: 'linear-gradient(90deg, #5D1AFA, #8D42FA 50%, #FE4DE1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '6px',
                }}
              >
                6 built in
              </p>
              <p style={{ font: "500 11.5px/1.4 'Manrope', sans-serif", color: 'rgba(22,27,50,0.42)' }}>
                Yours to keep after the course.
              </p>
            </div>

            {/* Card 4 — Career layer */}
            <div
              style={{
                position: 'absolute',
                top: '510px',
                left: '420px',
                width: '224px',
                background: '#fff',
                borderRadius: '18px',
                padding: '16px 18px 15px',
                boxShadow: '0 12px 40px rgba(22,27,50,0.11)',
              }}
              className="cohort-card"
            >
              <p style={{ font: "700 10px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.4)', textTransform: 'uppercase', marginBottom: '13px' }}>
                Your career layer
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', marginBottom: '11px' }}>
                <span style={{ font: "700 13px/1 'Manrope', sans-serif", color: '#161B32' }}>✓ Resume</span>
                <span style={{ font: "500 13px/1 'Manrope', sans-serif", color: '#161B32' }}>◐ Portfolio</span>
                <span style={{ font: "500 13px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.42)' }}>○ Interview prep</span>
              </div>
              <p style={{ font: "500 11px/1.4 'Manrope', sans-serif", color: 'rgba(22,27,50,0.38)' }}>
                Built with you, all cohort long.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. The Challenge Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#fff',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: "700 11px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.45)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            marginBottom: '20px',
          }}
        >
          The Challenge
        </p>
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#161B32',
            maxWidth: '780px',
            margin: '0 0 56px',
          }}
          className="cohort-h2"
        >
          Being a good PMM in India is hard. Becoming one is harder.
        </h2>

        <div style={{ display: 'flex', gap: '28px' }} className="cohort-challenge-cards">
          {/* Card 1 */}
          <div style={{ flex: 1, background: '#F6F2FF', borderRadius: '16px', padding: '40px' }}>
            <svg width="40" height="40" viewBox="0 0 40 40" style={{ marginBottom: '24px' }}>
              <defs>
                <linearGradient id="ic1" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#5D1AFA" />
                  <stop offset="100%" stopColor="#FC9D5C" />
                </linearGradient>
              </defs>
              <rect x="6" y="8" width="13" height="24" rx="2" fill="url(#ic1)" opacity="0.9" />
              <rect x="21" y="8" width="13" height="24" rx="2" fill="url(#ic1)" opacity="0.5" />
            </svg>
            <h3
              style={{
                font: "800 19px/1.25 'Manrope', sans-serif",
                color: '#161B32',
                margin: '0 0 14px',
              }}
            >
              The learning is generic.
            </h3>
            <p
              style={{
                font: "500 15px/1.65 'Manrope', sans-serif",
                color: 'rgba(22,27,50,0.65)',
                margin: 0,
              }}
            >
              Frameworks taught in the abstract, never grounded in how Indian companies actually build, sell, and grow.
            </p>
          </div>

          {/* Card 2 */}
          <div style={{ flex: 1, background: '#F6F2FF', borderRadius: '16px', padding: '40px' }}>
            <svg width="40" height="40" viewBox="0 0 40 40" style={{ marginBottom: '24px' }}>
              <defs>
                <linearGradient id="ic2" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#5D1AFA" />
                  <stop offset="100%" stopColor="#FC9D5C" />
                </linearGradient>
              </defs>
              <circle cx="20" cy="13" r="6" fill="url(#ic2)" />
              <path
                d="M8 34c0-6.6 5.4-12 12-12s12 5.4 12 12"
                stroke="url(#ic2)"
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
                opacity="0.5"
              />
            </svg>
            <h3
              style={{
                font: "800 19px/1.25 'Manrope', sans-serif",
                color: '#161B32',
                margin: '0 0 14px',
              }}
            >
              The execution is lonely.
            </h3>
            <p
              style={{
                font: "500 15px/1.65 'Manrope', sans-serif",
                color: 'rgba(22,27,50,0.65)',
                margin: 0,
              }}
            >
              You learn a framework, then you are on your own to turn it into real research, real positioning, a real GTM plan.
            </p>
          </div>

          {/* Card 3 */}
          <div style={{ flex: 1, background: '#F6F2FF', borderRadius: '16px', padding: '40px' }}>
            <svg width="40" height="40" viewBox="0 0 40 40" style={{ marginBottom: '24px' }}>
              <defs>
                <linearGradient id="ic3" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#5D1AFA" />
                  <stop offset="100%" stopColor="#FC9D5C" />
                </linearGradient>
              </defs>
              <rect x="9" y="6" width="22" height="30" rx="3" fill="url(#ic3)" opacity="0.18" />
              <rect x="9" y="6" width="22" height="30" rx="3" stroke="url(#ic3)" strokeWidth="2.2" fill="none" />
            </svg>
            <h3
              style={{
                font: "800 19px/1.25 'Manrope', sans-serif",
                color: '#161B32',
                margin: '0 0 14px',
              }}
            >
              The hiring is opaque.
            </h3>
            <p
              style={{
                font: "500 15px/1.65 'Manrope', sans-serif",
                color: 'rgba(22,27,50,0.65)',
                margin: 0,
              }}
            >
              No way to show your work, and no line to the people doing the hiring.
            </p>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p
            style={{
              font: "italic 400 34px/1.3 'Georgia', serif",
              color: '#161B32',
              maxWidth: '720px',
              margin: '0 auto',
            }}
          >
            Cohort 3 closes all three gaps in one place.
          </p>
        </div>
      </div>

      {/* 3. Testimonials Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#FCFBFF',
          padding: '100px 72px',
          textAlign: 'center',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: "700 11px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.45)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            marginBottom: '20px',
          }}
        >
          Testimonials
        </p>
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#161B32',
            margin: '0 0 64px',
          }}
          className="cohort-h2"
        >
          Two cohorts in. Here is where they are.
        </h2>

        {/* Featured photo */}
        <img
          alt={`${active?.name} photo large`}
          src={active?.photo}
          style={{
            width: '110px',
            height: '110px',
            borderRadius: '50%',
            background: '#F6F2FF',
            border: '4px solid #fff',
            boxShadow: '0 8px 32px rgba(22,27,50,0.14)',
            objectFit: 'cover',
            display: 'block',
            margin: '0 auto 36px',
          }}
        />

        {/* Featured quote */}
        <p
          style={{
            font: "italic 400 26px/1.55 'Instrument Serif', Georgia, serif",
            color: '#161B32',
            maxWidth: '640px',
            margin: '0 auto 28px',
          }}
        >
          &ldquo;{active?.quote}&rdquo;
        </p>

        <p
          style={{
            font: "800 17px/1 'Manrope', sans-serif",
            color: '#161B32',
            margin: '0 0 6px',
          }}
        >
          {active?.name}
        </p>
        <p
          style={{
            font: "500 13px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.45)',
            margin: '0 0 48px',
          }}
        >
          {active?.role}
        </p>

        {/* Avatar thumbnails */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '20px',
            marginBottom: '16px',
          }}
        >
          {testimonials?.map((t, i) => (
            <img
              key={t?.key}
              alt={`${t?.name} photo thumbnail`}
              src={t?.photo}
              onClick={() => setActiveTestimonial(i)}
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: activeTestimonial === i ? '3px solid #8D42FA' : '3px solid transparent',
                opacity: activeTestimonial === i ? 1 : 0.55,
                cursor: 'pointer',
                transition: 'border 0.2s, opacity 0.2s',
              }}
            />
          ))}
        </div>
        <p
          style={{
            font: "500 12px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.38)',
            margin: 0,
          }}
        >
          Anannya · Omkar · Sahar
        </p>
      </div>

      {/* 4. Who Should Apply — Three Kinds of People Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#FFF6EF',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#161B32',
            textAlign: 'center',
            margin: '0 0 64px',
          }}
        >
          Three kinds of people join this cohort.
        </h2>

        <div style={{ display: 'flex' }} className="cohort-three-people">
          {/* Column 1: The Switcher */}
          <div style={{ flex: 1, paddingRight: '36px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span
                style={{
                  font: "800 34px/1 'Manrope', sans-serif",
                  background: 'linear-gradient(135deg,#5D1AFA,#8D42FA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                01
              </span>
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg,#5D1AFA,#8D42FA)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <circle cx="9" cy="9" r="7" stroke="#fff" strokeWidth="1.4" fill="none" />
                  <circle cx="9" cy="9" r="2" fill="#fff" />
                </svg>
              </div>
            </div>
            <h3 style={{ font: "800 19px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
              The switcher.
            </h3>
            <div
              style={{
                width: '36px',
                height: '3px',
                borderRadius: '2px',
                background: 'linear-gradient(135deg,#5D1AFA,#8D42FA)',
                marginBottom: '14px',
              }}
            />
            <p style={{ font: "500 14px/1.65 'Manrope', sans-serif", color: 'rgba(22,27,50,0.65)', margin: 0 }}>
              You are a marketer (content, growth, brand) who wants the PMM seat. The curriculum gives you the full skill stack, the capstone gives you proof of work, and the career layer turns both into interviews.
            </p>
          </div>

          {/* Column 2: The Early PMM */}
          <div
            style={{
              flex: 1,
              padding: '0 36px',
              borderLeft: '1px solid rgba(22,27,50,0.12)',
              borderRight: '1px solid rgba(22,27,50,0.12)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span
                style={{
                  font: "800 34px/1 'Manrope', sans-serif",
                  background: 'linear-gradient(135deg,#8D42FA,#FE4DE1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                02
              </span>
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg,#8D42FA,#FE4DE1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path d="M9 15V8" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M9 8C9 8 6 7 5 4c2 0 4 1 4 4z" fill="#fff" opacity="0.9" />
                </svg>
              </div>
            </div>
            <h3 style={{ font: "800 19px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
              The early PMM.
            </h3>
            <div
              style={{
                width: '36px',
                height: '3px',
                borderRadius: '2px',
                background: 'linear-gradient(135deg,#8D42FA,#FE4DE1)',
                marginBottom: '14px',
              }}
            />
            <p style={{ font: "500 14px/1.65 'Manrope', sans-serif", color: 'rgba(22,27,50,0.65)', margin: 0 }}>
              You have the title, and you are building the foundation under it. The sessions give you frameworks your first years should have come with, and the weekly accountability loop pressure-tests your work early.
            </p>
          </div>

          {/* Column 3: The Working PMM */}
          <div style={{ flex: 1, paddingLeft: '36px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span
                style={{
                  font: "800 34px/1 'Manrope', sans-serif",
                  background: 'linear-gradient(135deg,#FE4DE1,#FC9D5C)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                03
              </span>
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg,#FE4DE1,#FC9D5C)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <circle cx="9" cy="9" r="7" stroke="#fff" strokeWidth="1.4" fill="none" />
                  <circle cx="9" cy="9" r="1.5" fill="#fff" />
                </svg>
              </div>
            </div>
            <h3 style={{ font: "800 19px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
              The working PMM.
            </h3>
            <div
              style={{
                width: '36px',
                height: '3px',
                borderRadius: '2px',
                background: 'linear-gradient(135deg,#FE4DE1,#FC9D5C)',
                marginBottom: '14px',
              }}
            />
            <p style={{ font: "500 14px/1.65 'Manrope', sans-serif", color: 'rgba(22,27,50,0.65)', margin: 0 }}>
              You know the job. You want sharper craft and better leverage. The AI workflows compress your execution, and the mentor room gives you the peer set most PMMs in India never get.
            </p>
          </div>
        </div>

        <p
          style={{
            font: "500 14px/1.7 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.58)',
            textAlign: 'center',
            maxWidth: '760px',
            margin: '48px auto 30px',
          }}
        >
          Every cohort so far has been a mix of all three. Capstone teams are matched to your career interests, you build with people heading the same direction.
        </p>
        <p
          style={{
            font: "italic 400 21px/1.4 'Instrument Serif', Georgia, serif",
            color: '#161B32',
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto',
          }}
        >
          If you want a library of videos to watch someday, this is the wrong course. Every week ends in work you defend live.
        </p>
      </div>

      {/* 5. How Cohort 3 Works Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#F6F2FF',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: "700 11px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.45)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          How Cohort 3 Works
        </p>
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#161B32',
            textAlign: 'center',
            margin: '0 0 64px',
          }}
        >
          Learn on your schedule. Grow with your cohort.
        </h2>

        {/* Four step cards */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }} className="cohort-steps-row">
          {/* Step 1 */}
          <div
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: '16px',
              padding: '32px 26px',
              boxShadow: '0 4px 24px rgba(22,27,50,0.07)',
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#5D1AFA,#8D42FA)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <span style={{ font: "800 15px/1 'Manrope', sans-serif", color: '#fff' }}>1</span>
            </div>
            <h3 style={{ font: "800 16px/1.3 'Manrope', sans-serif", color: '#161B32', margin: '0 0 14px' }}>
              Tuesday: New session drops
            </h3>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '12px' }} />
            <p style={{ font: "500 13px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.50)', margin: 0 }}>
              Videos · frameworks · exercises · AI workflow
            </p>
          </div>

          {/* Arrow 1 */}
          <div
            className="cohort-step-arrow"
            style={{
              flexShrink: 0,
              width: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '36px',
              color: '#8D42FA',
              fontSize: '20px',
            }}
          >
            →
          </div>

          {/* Step 2 */}
          <div
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: '16px',
              padding: '32px 26px',
              boxShadow: '0 4px 24px rgba(22,27,50,0.07)',
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#8D42FA,#FE4DE1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <span style={{ font: "800 15px/1 'Manrope', sans-serif", color: '#fff' }}>2</span>
            </div>
            <h3 style={{ font: "800 16px/1.3 'Manrope', sans-serif", color: '#161B32', margin: '0 0 12px' }}>
              Your week: Learn at your pace
            </h3>
            <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.58)', margin: 0 }}>
              You decide when and how. Every section ends with a quick exercise.
            </p>
          </div>

          {/* Arrow 2 */}
          <div
            className="cohort-step-arrow"
            style={{
              flexShrink: 0,
              width: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '36px',
              color: '#FE4DE1',
              fontSize: '20px',
            }}
          >
            →
          </div>

          {/* Step 3 */}
          <div
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: '16px',
              padding: '32px 26px',
              boxShadow: '0 4px 24px rgba(22,27,50,0.07)',
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#FE4DE1,#FC9D5C)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <span style={{ font: "800 15px/1 'Manrope', sans-serif", color: '#fff' }}>3</span>
            </div>
            <h3 style={{ font: "800 16px/1.3 'Manrope', sans-serif", color: '#161B32', margin: '0 0 14px' }}>
              Next Tuesday: Live accountability session
            </h3>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '12px' }} />
            <p style={{ font: "500 13px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.50)', margin: 0 }}>
              Assignment reviews · mentor Q&amp;A · peer discussion, then the next session unlocks
            </p>
          </div>

          {/* Arrow 3 */}
          <div
            className="cohort-step-arrow"
            style={{
              flexShrink: 0,
              width: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '36px',
              color: '#FC9D5C',
              fontSize: '20px',
            }}
          >
            →
          </div>

          {/* Step 4 */}
          <div
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: '16px',
              padding: '32px 26px',
              boxShadow: '0 4px 24px rgba(22,27,50,0.07)',
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#5D1AFA,#FC9D5C)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <span style={{ font: "800 15px/1 'Manrope', sans-serif", color: '#fff' }}>4</span>
            </div>
            <h3 style={{ font: "800 16px/1.3 'Manrope', sans-serif", color: '#161B32', margin: '0 0 12px' }}>
              Thursdays: Expert &amp; career sessions
            </h3>
            <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.58)', margin: 0 }}>
              Live conversations with PMM leaders and hiring managers.
            </p>
          </div>
        </div>

        {/* 60-day arc */}
        <h3
          style={{
            font: "800 30px/1.2 'Manrope', sans-serif",
            color: '#161B32',
            textAlign: 'center',
            margin: '76px 0 52px',
          }}
        >
          The 60-day arc
        </h3>

        <div style={{ position: 'relative' }} className="cohort-arc-wrapper">
          {/* Gradient line */}
          <div
            className="cohort-arc-line"
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              right: '10px',
              height: '2px',
              background: 'linear-gradient(90deg,#5D1AFA,#8D42FA,#FE4DE1,#FC9D5C)',
              borderRadius: '999px',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }} className="cohort-arc-timeline">
            {/* Milestone 1 */}
            <div style={{ maxWidth: '300px' }} className="cohort-arc-milestone">
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg,#5D1AFA,#8D42FA)',
                  marginBottom: '22px',
                }}
              />
              <p style={{ font: "800 15px/1.3 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
                Weeks 1–6: The learning loop.
              </p>
              <p style={{ font: "500 13px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.55)', margin: 0 }}>
                Six sessions, one per week, on the rhythm above
              </p>
            </div>
            {/* Dotted arrow 1→2 (mobile only) */}
            <div
              className="cohort-arc-arrow"
              style={{
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px 0',
                gap: '3px',
              }}
            >
              <div style={{ width: '2px', height: '28px', borderLeft: '2px dashed #8D42FA', opacity: 0.6 }} />
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 8L0 0H12L6 8Z" fill="#8D42FA" fillOpacity="0.7" />
              </svg>
            </div>
            {/* Milestone 2 */}
            <div style={{ maxWidth: '300px' }} className="cohort-arc-milestone">
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg,#8D42FA,#FE4DE1)',
                  marginBottom: '22px',
                }}
              />
              <p style={{ font: "800 15px/1.3 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
                Weeks 7–9: Team capstone.
              </p>
              <p style={{ font: "500 13px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.55)', margin: 0 }}>
                A real business problem from a real company, presented at Demo Day
              </p>
            </div>
            {/* Dotted arrow 2→3 (mobile only) */}
            <div
              className="cohort-arc-arrow"
              style={{
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px 0',
                gap: '3px',
              }}
            >
              <div style={{ width: '2px', height: '28px', borderLeft: '2px dashed #FE4DE1', opacity: 0.6 }} />
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 8L0 0H12L6 8Z" fill="#FE4DE1" fillOpacity="0.7" />
              </svg>
            </div>
            {/* Milestone 3 */}
            <div style={{ maxWidth: '300px' }} className="cohort-arc-milestone">
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg,#FE4DE1,#FC9D5C)',
                  marginBottom: '22px',
                }}
              />
              <p style={{ font: "800 15px/1.3 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
                Throughout: The career system.
              </p>
              <p style={{ font: "500 13px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.55)', margin: 0 }}>
                AI career workflows, resume and portfolio work, Talent Hub access, Thursday career sessions
              </p>
            </div>
          </div>
        </div>

        {/* Closing italic quote */}
        <p
          style={{
            font: "italic 400 30px/1.4 'Instrument Serif', Georgia, serif",
            color: '#161B32',
            textAlign: 'center',
            maxWidth: '740px',
            margin: '72px auto 0',
          }}
        >
          A full week with every session. Live support every week. 5–7 hours, on your own clock.
        </p>
      </div>

      {/* 6. Learn from the Best / People in the Room Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#161B32',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: '700 11px/1 Manrope,sans-serif',
            color: 'rgba(255,255,255,0.38)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          Learn from the best
        </p>
        <h2
          style={{
            font: '800 52px/1.1 Manrope,sans-serif',
            color: '#fff',
            textAlign: 'center',
            margin: '0 0 20px',
          }}
        >
          The people in the room.
        </h2>
        <p
          style={{
            font: '500 17px/1.65 Manrope,sans-serif',
            color: 'rgba(255,255,255,0.55)',
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto 72px',
          }}
        >
          The course is led by Varun Trivedi and Vipul Nanda, practitioners with two decades across India&apos;s fastest-growing companies. And every cohort brings in leaders who are shaping PMM in India for the Thursday sessions.
        </p>

        <div style={{ display: 'flex', gap: '28px' }} className="cohort-instructors-row">
          <div style={{ flex: 1 }}>
            <div
              style={{
                width: '100%',
                borderRadius: '20px',
                background: 'radial-gradient(ellipse at 50% 60%, rgba(252,157,92,0.55) 0%, rgba(254,77,225,0.35) 40%, rgba(93,26,250,0.5) 100%), #1e1640',
                padding: '52px 0 40px',
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '28px',
              }}
            >
              <img
                src="/assets/images/Group_2085662748_1_-1770136976594.png"
                alt="Varun Trivedi photo"
                style={{
                  width: '280px',
                  height: '280px',
                  borderRadius: '50%',
                  border: '6px solid #fff',
                  objectFit: 'cover',
                  background: 'linear-gradient(135deg,#FC9D5C,#FE4DE1,#8D42FA)',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ font: '800 26px/1 Manrope,sans-serif', color: '#fff' }}>Varun Trivedi</span>
              <a href="https://www.linkedin.com/in/varun-k-trivedi/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" style={{ opacity: 0.5 }}>
                  <rect width="20" height="20" rx="4" fill="white" opacity="0.15" />
                  <path d="M6 8.5V14M6 6V6.5M10 14V11c0-1.4.9-2.5 2-2.5s2 1.1 2 2.5v3M10 8.5V14" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
            <p
              style={{
                font: '700 11px/1 Manrope,sans-serif',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                background: 'linear-gradient(90deg,#5D1AFA,#8D42FA,#FE4DE1,#FC9D5C)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: '0 0 14px',
              }}
            >
              Founder, GoPMM
            </p>
            <p
              style={{
                font: '500 14px/1.6 Manrope,sans-serif',
                color: 'rgba(255,255,255,0.65)',
                margin: 0,
              }}
            >
              Varun has built product marketing from scratch across multiple companies and stages, learning PMM the way it really sticks: by shipping, testing, and fixing what doesn&apos;t work. He&apos;s led Product Marketing at LottieFiles and Cashfree Payments, and built IconScout from zero to 3M users. GoPMM grew out of his belief that PMMs shouldn&apos;t have to figure this craft out alone.
            </p>
          </div>

          {/* Vipul Nanda */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                width: '100%',
                borderRadius: '20px',
                background: 'radial-gradient(ellipse at 50% 60%, rgba(93,26,250,0.55) 0%, rgba(141,66,250,0.35) 40%, rgba(254,77,225,0.5) 100%), #1a1535',
                padding: '52px 0 40px',
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '28px',
              }}
            >
              <img
                src="/assets/images/Group_2085662750_1_-1770138667806.png"
                alt="Vipul Nanda photo"
                style={{
                  width: '280px',
                  height: '280px',
                  borderRadius: '50%',
                  border: '6px solid #fff',
                  objectFit: 'cover',
                  background: 'linear-gradient(135deg,#5D1AFA,#8D42FA,#FE4DE1)',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ font: '800 26px/1 Manrope,sans-serif', color: '#fff' }}>Vipul Nanda</span>
              <a href="https://www.linkedin.com/in/nandavipul/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" style={{ opacity: 0.5 }}>
                  <rect width="20" height="20" rx="4" fill="white" opacity="0.15" />
                  <path d="M6 8.5V14M6 6V6.5M10 14V11c0-1.4.9-2.5 2-2.5s2 1.1 2 2.5v3M10 8.5V14" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
            <p
              style={{
                font: '700 11px/1 Manrope,sans-serif',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                background: 'linear-gradient(90deg,#5D1AFA,#8D42FA,#FE4DE1,#FC9D5C)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: '0 0 14px',
              }}
            >
              Founder, GoPMM
            </p>
            <p
              style={{
                font: '500 14px/1.6 Manrope,sans-serif',
                color: 'rgba(255,255,255,0.65)',
                margin: 0,
              }}
            >
              Vipul approaches product marketing as a discipline of navigation: making sense of ambiguity and charting a path from where a product is today to where it can win tomorrow. He currently leads marketing at Databahn, where he helped take the company to Series A in under 12 months while driving 400% growth in a new market.
            </p>
          </div>
        </div>

        {/* Past cohort mentor row */}
        <p
          style={{
            font: '500 15px/1.65 Manrope,sans-serif',
            color: 'rgba(255,255,255,0.55)',
            textAlign: 'center',
            margin: '80px 0 32px',
          }}
        >
          A snapshot of the leaders who have shown up for past cohorts.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <img
              src="/assets/images/idmj05ptAq_1783324639535-1783324951414.jpeg"
              alt="Swiggy mentor photo"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                border: '1.5px solid rgba(255,255,255,0.12)',
                objectFit: 'cover',
              }}
            />
            <span style={{ font: '500 11px/1.3 Manrope,sans-serif', color: 'rgba(255,255,255,0.35)' }}>Swiggy</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <img
              src="/assets/images/idflmBZxmp_1783324652061-1783325115043.jpeg"
              alt="Razorpay mentor photo"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                border: '1.5px solid rgba(255,255,255,0.12)',
                objectFit: 'cover',
              }}
            />
            <span style={{ font: '500 11px/1.3 Manrope,sans-serif', color: 'rgba(255,255,255,0.35)' }}>Razorpay</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <img
              src="/assets/images/Freshworks_Inc_idGK6bEIM__1-1783325241689.png"
              alt="Freshworks mentor photo"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                border: '1.5px solid rgba(255,255,255,0.12)',
                objectFit: 'cover',
              }}
            />
            <span style={{ font: '500 11px/1.3 Manrope,sans-serif', color: 'rgba(255,255,255,0.35)' }}>Freshworks</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <img
              src="/assets/images/Sprinklr_idulhMm4cz_1-1783325349803.png"
              alt="Sprinklr mentor photo"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                border: '1.5px solid rgba(255,255,255,0.12)',
                objectFit: 'cover',
              }}
            />
            <span style={{ font: '500 11px/1.3 Manrope,sans-serif', color: 'rgba(255,255,255,0.35)' }}>Sprinklr</span>
          </div>
        </div>
      </div>

      {/* 7. Inside a Session Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#fff',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: "700 11px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.45)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          Inside a Session
        </p>
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#161B32',
            textAlign: 'center',
            margin: '0 0 20px',
          }}
          className="cohort-h2"
        >
          Not a lecture. A working system.
        </h2>
        <p
          style={{
            font: "500 17px/1.65 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.60)',
            textAlign: 'center',
            maxWidth: '680px',
            margin: '0 auto 72px',
          }}
        >
          Every session arrives as a complete workspace, the same page our current cohort works from.
        </p>

        <div style={{ display: 'flex', gap: '80px' }} className="cohort-inside-session">
          {/* Left column — four steps */}
          <div style={{ flex: '0 0 44%' }}>
            {/* Step 1: Watch */}
            <div style={{ paddingBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <svg width="24" height="24" viewBox="0 0 28 28">
                  <defs>
                    <linearGradient id="si1" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#5D1AFA" />
                      <stop offset="100%" stopColor="#8D42FA" />
                    </linearGradient>
                  </defs>
                  <circle cx="14" cy="14" r="12" stroke="url(#si1)" strokeWidth="1.8" fill="none" />
                  <path d="M11 10.5l7 3.5-7 3.5V10.5Z" stroke="url(#si1)" strokeWidth="1.6" fill="none" />
                </svg>
                <h3 style={{ font: "800 18px/1.3 'Manrope', sans-serif", color: '#161B32', margin: 0 }}>Watch.</h3>
              </div>
              <p style={{ font: "500 15px/1.65 'Manrope', sans-serif", color: 'rgba(22,27,50,0.62)', paddingLeft: '36px', margin: 0 }}>
                The session video, broken into module-length sections. No two-hour sits: each module is a focused chunk you can finish in one stretch.
              </p>
            </div>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.09)', marginBottom: '32px' }} />

            {/* Step 2: Work through */}
            <div style={{ paddingBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <svg width="24" height="24" viewBox="0 0 28 28">
                  <defs>
                    <linearGradient id="si2" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#8D42FA" />
                      <stop offset="100%" stopColor="#FE4DE1" />
                    </linearGradient>
                  </defs>
                  <rect x="7" y="7" width="14" height="14" rx="2.5" stroke="url(#si2)" strokeWidth="1.8" fill="none" />
                  <line x1="10" y1="11.5" x2="18" y2="11.5" stroke="url(#si2)" strokeWidth="1.5" />
                  <line x1="10" y1="14.5" x2="15" y2="14.5" stroke="url(#si2)" strokeWidth="1.5" />
                </svg>
                <h3 style={{ font: "800 18px/1.3 'Manrope', sans-serif", color: '#161B32', margin: 0 }}>Work through.</h3>
              </div>
              <p style={{ font: "500 15px/1.65 'Manrope', sans-serif", color: 'rgba(22,27,50,0.62)', paddingLeft: '36px', margin: 0 }}>
                Every module pairs its video section with a mini exercise. Answer it in plain English, check your understanding, move on. By the last module, you have built every piece the session needs.
              </p>
            </div>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.09)', marginBottom: '32px' }} />

            {/* Step 3: Build */}
            <div style={{ paddingBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <svg width="24" height="24" viewBox="0 0 28 28">
                  <defs>
                    <linearGradient id="si3" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FE4DE1" />
                      <stop offset="100%" stopColor="#FC9D5C" />
                    </linearGradient>
                  </defs>
                  <path d="M14 3.5L15.8 9.5H22L17 13.5L18.8 19.5L14 16L9.2 19.5L11 13.5L6 9.5H12.2L14 3.5Z" stroke="url(#si3)" strokeWidth="1.7" fill="none" />
                </svg>
                <h3 style={{ font: "800 18px/1.3 'Manrope', sans-serif", color: '#161B32', margin: 0 }}>Build.</h3>
              </div>
              <p style={{ font: "500 15px/1.65 'Manrope', sans-serif", color: 'rgba(22,27,50,0.62)', paddingLeft: '36px', margin: 0 }}>
                Each session ends in a framework, and the session&apos;s AI workflow. Plug it into the LLM you already use, and it works with you: takes your exercise inputs, does the mechanical assembly, surfaces the insights, and hands you a finished, portfolio-ready output.
              </p>
            </div>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.09)', marginBottom: '32px' }} />

            {/* Step 4: Bring it Tuesday */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <svg width="24" height="24" viewBox="0 0 28 28">
                  <defs>
                    <linearGradient id="si4" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#5D1AFA" />
                      <stop offset="100%" stopColor="#FC9D5C" />
                    </linearGradient>
                  </defs>
                  <rect x="5" y="8" width="18" height="15" rx="2.5" stroke="url(#si4)" strokeWidth="1.8" fill="none" />
                  <line x1="9" y1="5.5" x2="9" y2="10.5" stroke="url(#si4)" strokeWidth="1.8" />
                  <line x1="19" y1="5.5" x2="19" y2="10.5" stroke="url(#si4)" strokeWidth="1.8" />
                </svg>
                <h3 style={{ font: "800 18px/1.3 'Manrope', sans-serif", color: '#161B32', margin: 0 }}>Bring it Tuesday.</h3>
              </div>
              <p style={{ font: "500 15px/1.65 'Manrope', sans-serif", color: 'rgba(22,27,50,0.62)', paddingLeft: '36px', margin: 0 }}>
                Your completed framework is what you present and pressure-test at the live accountability session.
              </p>
            </div>
          </div>

          {/* Right column — browser mockup card */}
          <div
            style={{
              flex: 1,
              background: '#fff',
              border: '1.5px solid rgba(22,27,50,0.10)',
              borderRadius: '16px',
              boxShadow: '0 8px 40px rgba(22,27,50,0.08)',
              overflow: 'hidden',
            }}
          >
            {/* Browser chrome bar */}
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid rgba(22,27,50,0.07)',
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                background: '#fafafa',
              }}
            >
              <div style={{ width: '11px', height: '11px', borderRadius: '50%', background: 'rgba(22,27,50,0.12)' }} />
              <div style={{ width: '11px', height: '11px', borderRadius: '50%', background: 'rgba(22,27,50,0.12)' }} />
              <div style={{ width: '11px', height: '11px', borderRadius: '50%', background: 'rgba(22,27,50,0.12)' }} />
            </div>
            {/* Card content */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Video placeholder */}
              <div
                style={{
                  width: '100%',
                  borderRadius: '10px',
                  background: '#EDE8FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '14px',
                  padding: '36px 24px',
                }}
              >
                {/* Gradient play button */}
                <div
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #8D42FA 0%, #5D1AFA 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 4px 16px rgba(93,26,250,0.30)',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 5l10 5-10 5V5Z" fill="#fff" />
                  </svg>
                </div>
                <span style={{ font: "600 15px/1.4 'Manrope', sans-serif", color: 'rgba(22,27,50,0.55)' }}>[Session video]</span>
              </div>

              {/* Concept module row */}
              <div
                style={{
                  width: '100%',
                  borderRadius: '10px',
                  border: '1px solid rgba(22,27,50,0.09)',
                  background: '#fff',
                  padding: '14px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {/* Module header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Chevron down */}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="rgba(22,27,50,0.45)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={{ font: "600 14px/1.4 'Manrope', sans-serif", color: 'rgba(22,27,50,0.70)' }}>[Concept module]</span>
                </div>
                {/* Mini exercise input */}
                <div
                  style={{
                    borderRadius: '7px',
                    background: '#F5F5F5',
                    padding: '10px 14px',
                  }}
                >
                  <span style={{ font: "500 13px/1.4 'Manrope', sans-serif", color: 'rgba(22,27,50,0.40)' }}>[Mini exercise]</span>
                </div>
              </div>

              {/* Framework card */}
              <div
                style={{
                  width: '100%',
                  borderRadius: '10px',
                  border: '1.5px solid rgba(22,27,50,0.09)',
                  background: '#fff',
                  padding: '16px 16px',
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ font: "700 14px/1.4 'Manrope', sans-serif", color: '#161B32' }}>[Framework]</span>
                  {/* Gradient star icon */}
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '9px',
                      background: 'linear-gradient(135deg, #8D42FA 0%, #FA4DE1 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M9 2l1.8 4.1L15 7l-3 2.9.7 4.1L9 12l-3.7 2 .7-4.1L3 7l4.2-.9L9 2Z" fill="#fff" />
                    </svg>
                  </div>
                </div>
                {/* Skeleton lines */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ height: '8px', borderRadius: '5px', background: '#E8E4F5', width: '38%' }} />
                    <div style={{ height: '8px', borderRadius: '5px', background: '#E8E4F5', width: '28%' }} />
                    <div style={{ height: '8px', borderRadius: '5px', background: '#E8E4F5', width: '22%' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ height: '8px', borderRadius: '5px', background: '#E8E4F5', width: '50%' }} />
                    <div style={{ height: '8px', borderRadius: '5px', background: '#E8E4F5', width: '32%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 8. The Curriculum Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: 'linear-gradient(135deg, rgba(93,26,250,0.07) 0%, rgba(141,66,250,0.04) 25%, rgba(255,255,255,0) 50%, rgba(254,77,225,0.04) 75%, rgba(252,157,92,0.07) 100%), #fff',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: '700 11px/1 Manrope, sans-serif',
            color: 'rgba(22,27,50,0.45)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          The Curriculum
        </p>
        <h2
          style={{
            font: '800 52px/1.1 Manrope, sans-serif',
            color: '#161B32',
            textAlign: 'center',
            margin: '0 0 64px',
          }}
          className="cohort-h2"
        >
          Six sessions. The full PMM skill stack.
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
          }}
          className="cohort-curriculum-grid"
        >
          {/* Session 01 */}
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 4px 24px rgba(93,26,250,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
              <span
                style={{
                  font: '800 13px/1 Manrope, sans-serif',
                  background: 'linear-gradient(90deg,#5D1AFA,#8D42FA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                01
              </span>
              <span style={{ color: 'rgba(22,27,50,0.3)' }}>·</span>
              <span style={{ font: '800 15px/1.2 Manrope, sans-serif', color: '#161B32' }}>Understanding Products</span>
            </div>
            <p style={{ font: 'italic 400 15px/1.4 "Instrument Serif", Georgia, serif', color: '#161B32', margin: '0 0 16px' }}>
              Build a strong product foundation.
            </p>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '14px' }}></div>
            <p style={{ font: '500 13px/1.6 Manrope, sans-serif', color: 'rgba(22,27,50,0.52)', margin: '0' }}>
              Customer and product thinking · pain points and solution states · market landscape · competitive analysis
            </p>
          </div>

          {/* Session 02 */}
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 4px 24px rgba(93,26,250,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
              <span
                style={{
                  font: '800 13px/1 Manrope, sans-serif',
                  background: 'linear-gradient(90deg,#8D42FA,#FE4DE1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                02
              </span>
              <span style={{ color: 'rgba(22,27,50,0.3)' }}>·</span>
              <span style={{ font: '800 15px/1.2 Manrope, sans-serif', color: '#161B32' }}>Market &amp; Competitor Research</span>
            </div>
            <p style={{ font: 'italic 400 15px/1.4 "Instrument Serif", Georgia, serif', color: '#161B32', margin: '0 0 16px' }}>
              Find opportunities before everyone else.
            </p>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '14px' }}></div>
            <p style={{ font: '500 13px/1.6 Manrope, sans-serif', color: 'rgba(22,27,50,0.52)', margin: '0' }}>
              ICP and segmentation · market sizing · competitor research · opportunity mapping
            </p>
          </div>

          {/* Session 03 */}
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 4px 24px rgba(93,26,250,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
              <span
                style={{
                  font: '800 13px/1 Manrope, sans-serif',
                  background: 'linear-gradient(90deg,#FE4DE1,#FC9D5C)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                03
              </span>
              <span style={{ color: 'rgba(22,27,50,0.3)' }}>·</span>
              <span style={{ font: '800 15px/1.2 Manrope, sans-serif', color: '#161B32' }}>User Research</span>
            </div>
            <p style={{ font: 'italic 400 15px/1.4 "Instrument Serif", Georgia, serif', color: '#161B32', margin: '0 0 16px' }}>
              Turn customer conversations into product decisions.
            </p>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '14px' }}></div>
            <p style={{ font: '500 13px/1.6 Manrope, sans-serif', color: 'rgba(22,27,50,0.52)', margin: '0' }}>
              Primary and secondary research · customer interviews · insight synthesis · research frameworks
            </p>
          </div>

          {/* Session 04 */}
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 4px 24px rgba(93,26,250,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
              <span
                style={{
                  font: '800 13px/1 Manrope, sans-serif',
                  background: 'linear-gradient(90deg,#5D1AFA,#FE4DE1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                04
              </span>
              <span style={{ color: 'rgba(22,27,50,0.3)' }}>·</span>
              <span style={{ font: '800 15px/1.2 Manrope, sans-serif', color: '#161B32' }}>Positioning &amp; Messaging</span>
            </div>
            <p style={{ font: 'italic 400 15px/1.4 "Instrument Serif", Georgia, serif', color: '#161B32', margin: '0 0 16px' }}>
              Create messaging people actually remember.
            </p>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '14px' }}></div>
            <p style={{ font: '500 13px/1.6 Manrope, sans-serif', color: 'rgba(22,27,50,0.52)', margin: '0' }}>
              Positioning · value propositions · messaging frameworks · narrative development
            </p>
          </div>

          {/* Session 05 */}
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 4px 24px rgba(93,26,250,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
              <span
                style={{
                  font: '800 13px/1 Manrope, sans-serif',
                  background: 'linear-gradient(90deg,#8D42FA,#FC9D5C)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                05
              </span>
              <span style={{ color: 'rgba(22,27,50,0.3)' }}>·</span>
              <span style={{ font: '800 15px/1.2 Manrope, sans-serif', color: '#161B32' }}>GTM &amp; Customer Lifecycle</span>
            </div>
            <p style={{ font: 'italic 400 15px/1.4 "Instrument Serif", Georgia, serif', color: '#161B32', margin: '0 0 16px' }}>
              Launch products with confidence.
            </p>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '14px' }}></div>
            <p style={{ font: '500 13px/1.6 Manrope, sans-serif', color: 'rgba(22,27,50,0.52)', margin: '0' }}>
              GTM planning · customer journey · product launches · growth and adoption
            </p>
          </div>

          {/* Session 06 */}
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 4px 24px rgba(93,26,250,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
              <span
                style={{
                  font: '800 13px/1 Manrope, sans-serif',
                  background: 'linear-gradient(90deg,#FE4DE1,#5D1AFA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                06
              </span>
              <span style={{ color: 'rgba(22,27,50,0.3)' }}>·</span>
              <span style={{ font: '800 15px/1.2 Manrope, sans-serif', color: '#161B32' }}>Product Strategy &amp; Enablement</span>
            </div>
            <p style={{ font: 'italic 400 15px/1.4 "Instrument Serif", Georgia, serif', color: '#161B32', margin: '0 0 16px' }}>
              Connect Product Marketing to business outcomes.
            </p>
            <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '14px' }}></div>
            <p style={{ font: '500 13px/1.6 Manrope, sans-serif', color: 'rgba(22,27,50,0.52)', margin: '0' }}>
              Sales enablement · strategic planning · success metrics · cross-functional execution
            </p>
          </div>
        </div>

        <p
          style={{
            font: 'italic 400 30px/1.4 "Instrument Serif", Georgia, serif',
            color: '#161B32',
            textAlign: 'center',
            maxWidth: '740px',
            margin: '56px auto 0',
          }}
        >
          Every session: video modules, exercises, a framework, and an AI workflow you keep.
        </p>
      </div>

      {/* 9. The AI Systems Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#fff',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: "700 11px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.45)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          The AI Systems
        </p>
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#161B32',
            textAlign: 'center',
            margin: '0 0 72px',
          }}
        >
          Frameworks are the thinking. AI does the assembly.
        </h2>

        {/* Large bordered card */}
        <div
          className="cohort-ai-card"
          style={{
            width: '100%',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(93,26,250,0.05) 0%, rgba(141,66,250,0.03) 30%, rgba(255,255,255,0) 60%, rgba(252,157,92,0.04) 100%), #fff',
            border: '1.5px solid rgba(93,26,250,0.08)',
            padding: '48px',
            display: 'flex',
            gap: '56px',
          }}
        >
          {/* Left column */}
          <div style={{ flex: '0 0 44%' }}>
            <h3
              style={{
                font: "800 24px/1.2 'Manrope', sans-serif",
                color: '#161B32',
                margin: '0 0 16px',
              }}
            >
              The execution workflows: one per session.
            </h3>
            <p
              style={{
                font: "500 15px/1.68 'Manrope', sans-serif",
                color: 'rgba(22,27,50,0.65)',
                margin: '0 0 28px',
              }}
            >
              Every session ends in a framework, and every framework ships with its own AI workflow. Plug it into the LLM you already use, it takes your exercise inputs, runs the mechanical work, and builds the finished output with you:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
              {[
                'Competitive and customer research compressed from hours to minutes',
                'Positioning and messaging drafts built from your own inputs, not templates',
                'GTM plans and launch assets assembled with AI as your thinking partner',
                'Enablement material your sales team can use the same day',
              ]?.map((text, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '11px' }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <defs>
                      <linearGradient id={`ac${i + 1}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#5D1AFA" />
                        <stop offset="100%" stopColor="#FC9D5C" />
                      </linearGradient>
                    </defs>
                    <circle cx="9" cy="9" r="9" fill={`url(#ac${i + 1})`} />
                    <polyline points="5,9.5 7.5,12 13,6.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                  </svg>
                  <span style={{ font: "500 14px/1.5 'Manrope', sans-serif", color: '#161B32' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — floating cards */}
          <div className="cohort-ai-floating" style={{ flex: 1, position: 'relative', minHeight: '340px' }}>
            {/* Works with your LLM */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '210px',
                background: '#fff',
                borderRadius: '16px',
                padding: '18px 20px',
                boxShadow: '0 8px 28px rgba(22,27,50,0.10)',
                transform: 'rotate(-2.5deg)',
              }}
            >
              <p
                style={{
                  font: "700 10px/1 'Manrope', sans-serif",
                  color: 'rgba(22,27,50,0.40)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 12px',
                }}
              >
                Works with your LLM
              </p>
              <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                <span style={{ background: '#F0EBFF', color: '#5D1AFA', font: "600 11px/1 'Manrope', sans-serif", padding: '5px 10px', borderRadius: '999px' }}>Claude</span>
                <span style={{ background: 'rgba(22,27,50,0.06)', color: '#161B32', font: "600 11px/1 'Manrope', sans-serif", padding: '5px 10px', borderRadius: '999px' }}>ChatGPT</span>
                <span style={{ background: '#E8F0FE', color: '#1a73e8', font: "600 11px/1 'Manrope', sans-serif", padding: '5px 10px', borderRadius: '999px' }}>Gemini</span>
              </div>
            </div>

            {/* Resume-to-offer */}
            <div
              style={{
                position: 'absolute',
                top: '80px',
                left: 0,
                width: '230px',
                background: '#fff',
                borderRadius: '16px',
                padding: '18px 20px',
                boxShadow: '0 8px 28px rgba(22,27,50,0.10)',
                transform: 'rotate(2deg)',
              }}
            >
              <p
                style={{
                  font: "700 10px/1 'Manrope', sans-serif",
                  color: 'rgba(22,27,50,0.40)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 12px',
                }}
              >
                Resume-to-offer
              </p>
              <p style={{ font: "500 12px/1.4 'Manrope', sans-serif", color: 'rgba(22,27,50,0.35)', textDecoration: 'line-through', margin: '0 0 6px' }}>
                [Before: generic marketing experience…]
              </p>
              <p style={{ font: "700 12px/1.4 'Manrope', sans-serif", color: '#161B32', margin: '0 0 8px' }}>
                [After: PMM-framed, ready to screen]
              </p>
              <p style={{ font: "500 10px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.38)', margin: 0 }}>
                Rewritten by your AI workflow.
              </p>
            </div>

            {/* Positioning doc */}
            <div
              style={{
                position: 'absolute',
                top: '170px',
                right: '20px',
                width: '200px',
                background: '#fff',
                borderRadius: '16px',
                padding: '18px 20px',
                boxShadow: '0 8px 28px rgba(22,27,50,0.10)',
                transform: 'rotate(-1.5deg)',
              }}
            >
              <p
                style={{
                  font: "700 10px/1 'Manrope', sans-serif",
                  color: 'rgba(22,27,50,0.40)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 12px',
                }}
              >
                Positioning doc
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <div style={{ height: '8px', width: '85%', background: '#F0EBFF', borderRadius: '999px' }} />
                <div style={{ height: '8px', width: '65%', background: '#F0EBFF', borderRadius: '999px' }} />
                <div style={{ height: '8px', width: '75%', background: '#F0EBFF', borderRadius: '999px' }} />
              </div>
            </div>

            {/* Competitive research */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: '30px',
                width: '205px',
                background: '#fff',
                borderRadius: '16px',
                padding: '18px 20px',
                boxShadow: '0 8px 28px rgba(22,27,50,0.10)',
                transform: 'rotate(2.5deg)',
              }}
            >
              <p
                style={{
                  font: "700 10px/1 'Manrope', sans-serif",
                  color: 'rgba(22,27,50,0.40)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 12px',
                }}
              >
                Competitive research
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <div style={{ height: '8px', width: '90%', background: '#F0EBFF', borderRadius: '999px' }} />
                <div style={{ height: '8px', width: '70%', background: '#F0EBFF', borderRadius: '999px' }} />
                <div style={{ height: '8px', width: '80%', background: '#F0EBFF', borderRadius: '999px' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Career Workflows Block */}
        <div style={{ height: '1px', background: 'rgba(22,27,50,0.08)', marginBottom: '64px', marginTop: '64px' }} />

        <h3 style={{ font: "800 30px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 20px' }}>
          The career workflows: yours for the job hunt and beyond.
        </h3>
        <p style={{ font: "500 16px/1.68 'Manrope', sans-serif", color: 'rgba(22,27,50,0.65)', maxWidth: '700px', margin: '0 0 40px' }}>
          A second set of systems, built for the career itself:
        </p>

        <div style={{ display: 'flex', gap: '24px' }} className="cohort-career-workflows">
          {/* Resume-to-offer */}
          <div style={{ flex: 1, background: '#F6F2FF', borderRadius: '16px', padding: '28px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg,#5D1AFA,#8D42FA)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <rect x="3" y="2" width="12" height="14" rx="2" stroke="#fff" strokeWidth="1.5" fill="none" />
                <line x1="6" y1="6" x2="12" y2="6" stroke="#fff" strokeWidth="1.2" />
                <line x1="6" y1="9" x2="12" y2="9" stroke="#fff" strokeWidth="1.2" />
                <line x1="6" y1="12" x2="9" y2="12" stroke="#fff" strokeWidth="1.2" />
              </svg>
            </div>
            <h4 style={{ font: "800 15px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 10px' }}>Resume-to-offer.</h4>
            <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: 0 }}>
              A workflow that takes your experience and rebuilds it in the language PMM hiring managers actually screen for.
            </p>
          </div>

          {/* Portfolio builder */}
          <div style={{ flex: 1, background: '#F6F2FF', borderRadius: '16px', padding: '28px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg,#8D42FA,#FE4DE1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <rect x="2" y="2" width="6" height="6" rx="1.5" stroke="#fff" strokeWidth="1.5" fill="none" />
                <rect x="10" y="2" width="6" height="6" rx="1.5" stroke="#fff" strokeWidth="1.5" fill="none" />
                <rect x="2" y="10" width="6" height="6" rx="1.5" stroke="#fff" strokeWidth="1.5" fill="none" />
                <rect x="10" y="10" width="6" height="6" rx="1.5" stroke="#fff" strokeWidth="1.5" fill="none" />
              </svg>
            </div>
            <h4 style={{ font: "800 15px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 10px' }}>Portfolio builder.</h4>
            <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: 0 }}>
              Turns your session frameworks and capstone work into case studies worth showing.
            </p>
          </div>

          {/* Interview prep */}
          <div style={{ flex: 1, background: '#F6F2FF', borderRadius: '16px', padding: '28px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg,#FE4DE1,#FC9D5C)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path d="M3 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2V4Z" stroke="#fff" strokeWidth="1.5" fill="none" />
              </svg>
            </div>
            <h4 style={{ font: "800 15px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 10px' }}>Interview prep.</h4>
            <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: 0 }}>
              Structured practice against the questions Indian PMM interviews actually ask.
            </p>
          </div>
        </div>

        <p
          style={{
            font: 'italic 400 30px/1.4 "Instrument Serif", Georgia, serif',
            color: '#161B32',
            textAlign: 'center',
            maxWidth: '760px',
            margin: '64px auto 0',
          }}
        >
          These are not classroom tools. They are systems you keep, and reuse on every product you ever market.
        </p>
      </div>

      {/* 10. The Capstone Project Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#161B32',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: "700 11px/1 'Manrope', sans-serif",
            color: 'rgba(255,255,255,0.38)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          The Capstone Project
        </p>
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#fff',
            textAlign: 'center',
            margin: '0 0 28px',
          }}
        >
          Where the thinking becomes proof.
        </h2>
        <p
          style={{
            font: "500 17px/1.68 'Manrope', sans-serif",
            color: 'rgba(255,255,255,0.58)',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 20px',
          }}
        >
          For the final 2.5 weeks, the sessions stop and the work begins. You join a team of peers matched to your career interests and take on a live business problem from a real company, the kind of high-stakes, ambiguous question a working PMM actually gets handed.
        </p>
        <p
          style={{
            font: "500 17px/1.68 'Manrope', sans-serif",
            color: 'rgba(255,255,255,0.58)',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 64px',
          }}
        >
          You research the market, talk to users and employees, and frame a full Product Marketing response, then present it at Demo Day, in front of a panel of industry leaders.
        </p>

        {/* Four company problem cards */}
        <div style={{ display: 'flex', gap: '20px' }} className="cohort-capstone-cards">
          <div
            style={{
              flex: 1,
              background: '#1D2340',
              borderRadius: '14px',
              borderTop: '3px solid #FC6011',
              padding: '24px',
            }}
          >
            <p style={{ font: "800 16px/1.2 'Manrope', sans-serif", color: '#fff', margin: '0 0 12px' }}>
              Swiggy Instamart
            </p>
            <p style={{ font: "500 14px/1.65 'Manrope', sans-serif", color: 'rgba(255,255,255,0.52)', margin: 0 }}>
              Go all-in on loyal metro shoppers, or win first-time buyers in Tier 2/3 towns?
            </p>
          </div>
          <div
            style={{
              flex: 1,
              background: '#1D2340',
              borderRadius: '14px',
              borderTop: '3px solid #25C16F',
              padding: '24px',
            }}
          >
            <p style={{ font: "800 16px/1.2 'Manrope', sans-serif", color: '#fff', margin: '0 0 12px' }}>
              Freshworks
            </p>
            <p style={{ font: "500 14px/1.65 'Manrope', sans-serif", color: 'rgba(255,255,255,0.52)', margin: 0 }}>
              Max out SMBs, crack enterprise, or go global, where next?
            </p>
          </div>
          <div
            style={{
              flex: 1,
              background: '#1D2340',
              borderRadius: '14px',
              borderTop: '3px solid #7C3AED',
              padding: '24px',
            }}
          >
            <p style={{ font: "800 16px/1.2 'Manrope', sans-serif", color: '#fff', margin: '0 0 12px' }}>
              Rocketlane
            </p>
            <p style={{ font: "500 14px/1.65 'Manrope', sans-serif", color: 'rgba(255,255,255,0.52)', margin: 0 }}>
              More seats, bigger clients, or new use cases, what is the fastest path to $100M?
            </p>
          </div>
          <div
            style={{
              flex: 1,
              background: '#1D2340',
              borderRadius: '14px',
              borderTop: '3px solid #00B8A9',
              padding: '24px',
            }}
          >
            <p style={{ font: "800 16px/1.2 'Manrope', sans-serif", color: '#fff', margin: '0 0 12px' }}>
              Groww
            </p>
            <p style={{ font: "500 14px/1.65 'Manrope', sans-serif", color: 'rgba(255,255,255,0.52)', margin: 0 }}>
              Deeper investments or broader money moves, where should the next big bet go?
            </p>
          </div>
        </div>

        <p
          style={{
            font: "500 12px/1 'Manrope', sans-serif",
            color: 'rgba(255,255,255,0.28)',
            textAlign: 'center',
            margin: '20px 0 64px',
          }}
        >
          Real problems from Cohort 1.
        </p>
        <p
          style={{
            font: "italic 400 30px/1.4 'Instrument Serif', Georgia, serif",
            color: '#fff',
            textAlign: 'center',
            maxWidth: '740px',
            margin: '0 auto',
          }}
        >
          You leave with portfolio-ready work, and the experience of defending it in the room.
        </p>
      </div>

      {/* 11. Career Support & The Talent Hub Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: '#fff',
          padding: '100px 72px',
        }}
        className="cohort-section-pad"
      >
        <p
          style={{
            font: "700 11px/1 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.45)',
            letterSpacing: '0.13em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          Career Support &amp; The Talent Hub
        </p>
        <h2
          style={{
            font: "800 52px/1.1 'Manrope', sans-serif",
            color: '#161B32',
            textAlign: 'center',
            margin: '0 0 16px',
          }}
        >
          The course ends. The career layer does not.
        </h2>
        <p
          style={{
            font: "500 16px/1.6 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.55)',
            textAlign: 'center',
            margin: '0 0 64px',
          }}
        >
          Exclusive to the cohort, running through all 60 days:
        </p>

        <div style={{ display: 'flex', gap: '72px' }} className="cohort-career-support">
          {/* Left — vertical timeline */}
          <div style={{ flex: 1, position: 'relative' }}>
            {/* Gradient vertical line */}
            <div
              style={{
                position: 'absolute',
                left: '20px',
                top: '20px',
                bottom: '20px',
                width: '2px',
                background: 'linear-gradient(to bottom, #5D1AFA, #8D42FA, #FE4DE1, #FC9D5C)',
                transform: 'translateX(-50%)',
              }}
            />

            {/* Item 1: Resume optimisation */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '36px', position: 'relative' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#fff',
                  border: '2.5px solid #5D1AFA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <rect x="2" y="1" width="12" height="14" rx="2" stroke="#5D1AFA" strokeWidth="1.5" fill="none" />
                  <line x1="5" y1="5" x2="11" y2="5" stroke="#5D1AFA" strokeWidth="1.2" />
                  <line x1="5" y1="8" x2="11" y2="8" stroke="#5D1AFA" strokeWidth="1.2" />
                </svg>
              </div>
              <div>
                <h4 style={{ font: "800 17px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
                  Resume optimisation.
                </h4>
                <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: 0 }}>
                  Your experience, rewritten with mentors and the resume-to-offer workflow into the language PMM hiring actually screens for.
                </p>
              </div>
            </div>

            {/* Item 2: Portfolio reviews */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '36px', position: 'relative' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#fff',
                  border: '2.5px solid #8D42FA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path d="M2 3a1 1 0 0 1 1-1h4l2 2h4a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3Z" stroke="#8D42FA" strokeWidth="1.5" fill="none" />
                </svg>
              </div>
              <div>
                <h4 style={{ font: "800 17px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
                  Portfolio reviews.
                </h4>
                <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: 0 }}>
                  Your session frameworks and capstone become reviewed, presentable case studies, proof of work, not claims of skill.
                </p>
              </div>
            </div>

            {/* Item 3: Interview preparation */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '36px', position: 'relative' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#fff',
                  border: '2.5px solid #FE4DE1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path d="M2 3a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H8l-4 3v-3H4a2 2 0 0 1-2-2V3Z" stroke="#FE4DE1" strokeWidth="1.5" fill="none" />
                </svg>
              </div>
              <div>
                <h4 style={{ font: "800 17px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
                  Interview preparation.
                </h4>
                <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: 0 }}>
                  Mock scenarios and structured practice built on real PMM interview banks.
                </p>
              </div>
            </div>

            {/* Item 4: Career guidance */}
            <div style={{ display: 'flex', gap: '20px', position: 'relative' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#fff',
                  border: '2.5px solid #FC9D5C',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="6" stroke="#FC9D5C" strokeWidth="1.5" fill="none" />
                  <line x1="8" y1="2" x2="8" y2="4" stroke="#FC9D5C" strokeWidth="1.5" />
                  <line x1="8" y1="12" x2="8" y2="14" stroke="#FC9D5C" strokeWidth="1.5" />
                </svg>
              </div>
              <div>
                <h4 style={{ font: "800 17px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 6px' }}>
                  Career guidance.
                </h4>
                <p style={{ font: "500 13.5px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: 0 }}>
                  Direct conversations about roles, hiring expectations, and your next move, with people who have made and hired for those moves.
                </p>
              </div>
            </div>
          </div>

          {/* Right — Talent Hub */}
          <div style={{ flex: 1 }}>
            <h3 style={{ font: "800 22px/1.2 'Manrope', sans-serif", color: '#161B32', margin: '0 0 8px' }}>
              And where it all points: the Talent Hub.
            </h3>
            <p style={{ font: "500 15px/1.6 'Manrope', sans-serif", color: 'rgba(22,27,50,0.60)', margin: '0 0 24px' }}>
              GoPMM&apos;s live hiring platform, open to the whole community, and busy:
            </p>

            {/* Browser mockup */}
            <div
              style={{
                background: '#fff',
                border: '1.5px solid rgba(22,27,50,0.10)',
                borderRadius: '14px',
                boxShadow: '0 8px 36px rgba(22,27,50,0.08)',
                overflow: 'hidden',
              }}
            >
              {/* Browser chrome */}
              <div
                style={{
                  padding: '11px 16px',
                  borderBottom: '1px solid rgba(22,27,50,0.07)',
                  background: '#fafafa',
                  display: 'flex',
                  gap: '7px',
                  alignItems: 'center',
                }}
              >
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(22,27,50,0.12)' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(22,27,50,0.12)' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(22,27,50,0.12)' }} />
                <span
                  style={{
                    font: "500 10px/1 'Manrope', sans-serif",
                    color: 'rgba(22,27,50,0.30)',
                    marginLeft: '8px',
                  }}
                >
                  gopmm.in/talent-hub
                </span>
              </div>

              {/* Stats row */}
              <div style={{ display: 'flex', borderBottom: '1px solid rgba(22,27,50,0.07)' }}>
                <div
                  style={{
                    flex: 1,
                    padding: '20px 16px',
                    textAlign: 'center',
                    borderRight: '1px solid rgba(22,27,50,0.07)',
                  }}
                >
                  <p style={{ font: "800 22px/1 'Manrope', sans-serif", color: '#161B32', margin: '0 0 5px' }}>300+</p>
                  <p style={{ font: "500 11px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.45)', margin: 0 }}>live roles</p>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: '20px 16px',
                    textAlign: 'center',
                    borderRight: '1px solid rgba(22,27,50,0.07)',
                  }}
                >
                  <p style={{ font: "800 22px/1 'Manrope', sans-serif", color: '#161B32', margin: '0 0 5px' }}>100+</p>
                  <p style={{ font: "500 11px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.45)', margin: 0 }}>profiles</p>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: '20px 16px',
                    textAlign: 'center',
                    borderRight: '1px solid rgba(22,27,50,0.07)',
                  }}
                >
                  <p style={{ font: "800 22px/1 'Manrope', sans-serif", color: '#161B32', margin: '0 0 5px' }}>90+</p>
                  <p style={{ font: "500 11px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.45)', margin: 0 }}>applications</p>
                </div>
                <div style={{ flex: 1, padding: '20px 16px', textAlign: 'center' }}>
                  <p
                    style={{
                      font: "800 22px/1 'Manrope', sans-serif",
                      background: 'linear-gradient(90deg, #5D1AFA, #FC9D5C)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      margin: '0 0 5px',
                    }}
                  >
                    Live
                  </p>
                  <p style={{ font: "500 11px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.45)', margin: 0 }}>recruiters hiring</p>
                </div>
              </div>

              {/* Role rows */}
              <div style={{ padding: '0 16px 8px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '14px 0',
                    borderBottom: '1px solid rgba(22,27,50,0.06)',
                  }}
                >
                  <span style={{ font: "600 13px/1 'Manrope', sans-serif", color: '#161B32' }}>Senior PMM, B2B SaaS</span>
                  <span style={{ font: "500 12px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.40)' }}>Bengaluru</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '14px 0',
                    borderBottom: '1px solid rgba(22,27,50,0.06)',
                  }}
                >
                  <span style={{ font: "600 13px/1 'Manrope', sans-serif", color: '#161B32' }}>Product Marketing Lead</span>
                  <span style={{ font: "500 12px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.40)' }}>Remote</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '14px 0',
                  }}
                >
                  <span style={{ font: "600 13px/1 'Manrope', sans-serif", color: '#161B32' }}>GTM Manager, Fintech</span>
                  <span style={{ font: "500 12px/1 'Manrope', sans-serif", color: 'rgba(22,27,50,0.40)' }}>Mumbai</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p
          style={{
            font: "500 15px/1.7 'Manrope', sans-serif",
            color: 'rgba(22,27,50,0.60)',
            textAlign: 'center',
            maxWidth: '820px',
            margin: '64px auto 0',
          }}
        >
          Anyone can join the Hub. Cohort members show up differently: a reviewed profile, a completed portfolio, capstone work as proof, the strongest version of yourself on a platform recruiters are already searching.
        </p>
      </div>

      {/* 12. Pricing CTA Section */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          background: 'linear-gradient(135deg,#5D1AFA 0%,#8D42FA 50%,#FE4DE1 100%)',
          padding: '100px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Decorative SVG shapes */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
          <rect x="80" y="60" width="9" height="9" rx="1" fill="#fff" opacity="0.7" transform="rotate(22 80 60)"/>
          <rect x="200" y="140" width="8" height="8" rx="1" fill="#FC9D5C" opacity="0.8" transform="rotate(-15 200 140)"/>
          <rect x="1300" y="80" width="10" height="10" rx="1" fill="#fff" opacity="0.65" transform="rotate(38 1300 80)"/>
          <rect x="1180" y="160" width="8" height="8" rx="1" fill="#FC9D5C" opacity="0.75" transform="rotate(-28 1180 160)"/>
          <rect x="120" y="780" width="9" height="9" rx="1" fill="#FC9D5C" opacity="0.7" transform="rotate(45 120 780)"/>
          <rect x="1350" y="760" width="9" height="9" rx="1" fill="#fff" opacity="0.7" transform="rotate(30 1350 760)"/>
          <circle cx="160" cy="200" r="5" fill="#fff" opacity="0.55"/>
          <circle cx="1280" cy="220" r="6" fill="#FC9D5C" opacity="0.6"/>
          <circle cx="90" cy="680" r="5" fill="#FC9D5C" opacity="0.55"/>
          <circle cx="1370" cy="650" r="6" fill="#fff" opacity="0.5"/>
        </svg>

        {/* Glassmorphism card */}
        <div
          className="cohort-pricing-card"
          style={{
            position: 'relative',
            width: '720px',
            borderRadius: '24px',
            background: 'rgba(255,255,255,0.13)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1.5px solid rgba(255,255,255,0.35)',
            boxShadow: '0 24px 64px rgba(22,27,50,0.25)',
            padding: '56px',
            overflow: 'hidden',
          }}
        >
          {/* Cohort 3 ribbon */}
          <div
            style={{
              position: 'absolute',
              top: '18px',
              right: '-36px',
              background: '#161B32',
              color: '#fff',
              font: "800 10px/1 'Manrope', sans-serif",
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '6px 40px',
              transform: 'rotate(45deg)',
              whiteSpace: 'nowrap',
            }}
          >
            Cohort 3
          </div>

          {/* Price heading */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ font: "800 32px/1.1 'Manrope', sans-serif", color: '#fff', margin: '0 0 16px' }}>
              One price. Everything you need.
            </h2>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <p style={{ font: "800 52px/1 'Manrope', sans-serif", color: '#fff', margin: 0 }}>₹49,999/-</p>
              <svg style={{ position: 'absolute', top: '-10px', right: '-22px' }} width="18" height="18" viewBox="0 0 18 18">
                <path d="M9 0L10.5 7.5L18 9L10.5 10.5L9 18L7.5 10.5L0 9L7.5 7.5L9 0Z" fill="#fff" opacity="0.9"/>
              </svg>
            </div>
          </div>

          {/* Feature list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '36px' }}>
            {[
              'The full 60-day journey: six self-paced sessions, weekly live accountability, expert Thursdays',
              'Every framework and AI workflow, execution and career, yours to keep',
              'The team capstone and Demo Day',
              'Resume, portfolio, and interview support, exclusive to the cohort',
              'Lifetime access to the course materials and the GoPMM community',
              'Certificate on completion, earned through your assignments and Tuesday sessions',
            ]?.map((text, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div
                  style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '1px',
                  }}
                >
                  <svg width="11" height="9" viewBox="0 0 11 9">
                    <defs>
                      <linearGradient id={`pc${i + 1}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#5D1AFA"/>
                        <stop offset="100%" stopColor="#FE4DE1"/>
                      </linearGradient>
                    </defs>
                    <polyline points="1,4.5 4,7.5 10,1" stroke={`url(#pc${i + 1})`} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </div>
                <span style={{ font: "500 15px/1.5 'Manrope', sans-serif", color: '#fff' }}>{text}</span>
              </div>
            ))}
          </div>

          {/* CTA button */}
          <a
            href="https://forms.gle/FRbwdLgRbGjdgMNs5"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              width: '100%',
              padding: '18px',
              borderRadius: '14px',
              background: '#fff',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            <span
              style={{
                background: 'linear-gradient(90deg,#5D1AFA 0%,#8D42FA 40%,#FE4DE1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                font: "800 15px/1 'Manrope', sans-serif",
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Apply for Cohort 3
            </span>
          </a>
          <p style={{ font: "500 13px/1 'Manrope', sans-serif", color: 'rgba(255,255,255,0.85)', textAlign: 'center', margin: '16px 0 0' }}>
            EMI available: pay in up to three installments.
          </p>
        </div>
      </div>

      {/* 13. FAQ Section */}
      <FAQSection />

      {/* 14. Final CTA Section */}
      <div style={{ position: 'relative', width: '100%', maxWidth: '1440px', margin: '0 auto', background: 'linear-gradient(135deg,#5D1AFA 0%,#8D42FA 50%,#FE4DE1 100%)', padding: '100px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
          <rect x="60" y="40" width="8" height="8" rx="1" fill="#fff" opacity="0.6" transform="rotate(20 60 40)"/>
          <rect x="1320" y="50" width="8" height="8" rx="1" fill="#FC9D5C" opacity="0.6" transform="rotate(35 1320 50)"/>
          <rect x="80" y="520" width="8" height="8" rx="1" fill="#FC9D5C" opacity="0.55" transform="rotate(42 80 520)"/>
          <rect x="1360" y="510" width="8" height="8" rx="1" fill="#fff" opacity="0.55" transform="rotate(28 1360 510)"/>
          <circle cx="130" cy="160" r="5" fill="#fff" opacity="0.45"/>
          <circle cx="1310" cy="180" r="5" fill="#FC9D5C" opacity="0.45"/>
        </svg>
        <div className="cohort-final-cta" style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '0 72px', maxWidth: '900px' }}>
          <p style={{ font: '700 11px/1 Manrope,sans-serif', color: 'rgba(255,255,255,0.90)', letterSpacing: '0.13em', textTransform: 'uppercase', margin: '0 0 24px' }}>Cohort 3 Starts August 18</p>
          <h2 style={{ font: '800 38px/1.15 Manrope,sans-serif', color: '#fff', maxWidth: '750px', margin: '0 0 20px' }}>Six sessions. One capstone. Every system you need, and a community that stays with you after.</h2>
          <p style={{ font: '500 16px/1.65 Manrope,sans-serif', color: 'rgba(255,255,255,0.90)', maxWidth: '520px', margin: '0 0 40px' }}>Fill in the form. We will get on a short call to make sure this is right for you. Then we begin.</p>
          <a href="https://forms.gle/FRbwdLgRbGjdgMNs5" target="_blank" rel="noopener noreferrer" style={{ marginBottom: '28px' }}>
            <button style={{ padding: '18px 48px', borderRadius: '14px', background: '#fff', border: 'none', cursor: 'pointer' }}>
              <span style={{ font: '800 14px/1 Manrope,sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', background: 'linear-gradient(90deg,#5D1AFA 0%,#8D42FA 40%,#FE4DE1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Apply for Cohort 3</span>
            </button>
          </a>
          <p style={{ font: '500 13px/1.7 Manrope,sans-serif', color: 'rgba(255,255,255,0.85)', margin: 0 }}>
            Not ready yet? Start free:{' '}
            <a href="https://gopmm.in/blogs/what-the-indian-job-market-actually-wants-from-its-pmms" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline', textUnderlineOffset: '3px' }}>read the PMM Jobs Report</a>{' '}
            or <a href="https://talent.gopmm.in/" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline', textUnderlineOffset: '3px' }}>create your Talent Hub profile</a>
          </p>
        </div>
      </div>

      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

        .font-manrope {
          font-family: 'Manrope', sans-serif;
        }

        @media (max-width: 900px) {
          /* ── Hero ── */
          .cohort-hero-inner {
            flex-direction: column !important;
            padding: 24px 20px 48px !important;
            height: auto !important;
          }
          .cohort-hero-inner > div:first-child {
            width: 100% !important;
          }
          .cohort-cards-col {
            display: none !important;
          }
          .cohort-h1 {
            font-size: 32px !important;
            line-height: 1.15 !important;
          }
          .cohort-h2 {
            font-size: 26px !important;
            line-height: 1.2 !important;
          }

          /* ── Hero outer container ── */
          .cohort-hero-outer {
            height: auto !important;
          }

          /* ── Hero ribbon ── */
          .cohort-ribbon {
            display: none !important;
          }

          /* ── Section padding ── */
          .cohort-section-pad {
            padding: 56px 20px !important;
          }

          /* ── Challenge cards ── */
          .cohort-challenge-cards {
            flex-direction: column !important;
            gap: 16px !important;
          }

          /* ── Three kinds of people ── */
          .cohort-three-people {
            flex-direction: column !important;
            gap: 32px !important;
          }
          .cohort-three-people > div {
            padding: 0 !important;
            border-left: none !important;
            border-right: none !important;
            border-top: 1px solid rgba(22,27,50,0.10) !important;
            padding-top: 28px !important;
          }
          .cohort-three-people > div:first-child {
            border-top: none !important;
            padding-top: 0 !important;
          }

          /* ── How it works steps ── */
          .cohort-steps-row {
            flex-direction: column !important;
            gap: 16px !important;
            align-items: stretch !important;
          }
          .cohort-step-arrow {
            display: none !important;
          }
          .cohort-steps-row > div[style*="flex: 1"] {
            flex: none !important;
            width: 100% !important;
            box-sizing: border-box !important;
            margin-bottom: 0 !important;
            min-height: 160px !important;
          }

          /* ── 60-day arc timeline ── */
          .cohort-arc-timeline {
            flex-direction: column !important;
            gap: 0 !important;
          }
          .cohort-arc-line {
            display: none !important;
          }
          .cohort-arc-dot {
            margin-bottom: 10px !important;
          }
          .cohort-arc-milestone {
            padding-bottom: 0 !important;
          }
          .cohort-arc-arrow {
            display: flex !important;
          }

          /* ── People in the room ── */
          .cohort-instructors-row {
            flex-direction: column !important;
            gap: 48px !important;
          }
          .cohort-instructor-photo {
            padding: 36px 0 28px !important;
          }
          .cohort-instructor-photo img {
            width: 200px !important;
            height: 200px !important;
          }
          .cohort-mentors-row {
            gap: 24px !important;
          }

          /* ── Inside a session ── */
          .cohort-inside-session {
            flex-direction: column !important;
            gap: 40px !important;
          }
          .cohort-inside-session > div:first-child {
            flex: none !important;
            width: 100% !important;
          }

          /* ── Curriculum grid ── */
          .cohort-curriculum-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }

          /* ── AI Systems large card ── */
          .cohort-ai-card {
            flex-direction: column !important;
            gap: 32px !important;
            padding: 28px 20px !important;
          }
          .cohort-ai-card > div:first-child {
            flex: none !important;
            width: 100% !important;
          }
          .cohort-ai-floating {
            display: none !important;
          }

          /* ── Career workflows 3-col ── */
          .cohort-career-workflows {
            flex-direction: column !important;
            gap: 16px !important;
          }

          /* ── Capstone company cards ── */
          .cohort-capstone-cards {
            flex-direction: column !important;
            gap: 12px !important;
          }

          /* ── Career support 2-col ── */
          .cohort-career-support {
            flex-direction: column !important;
            gap: 48px !important;
          }

          /* ── Pricing card ── */
          .cohort-pricing-card {
            width: calc(100% - 40px) !important;
            padding: 36px 24px !important;
          }

          /* ── Final CTA ── */
          .cohort-final-cta {
            padding: 0 20px !important;
          }
          .cohort-final-cta h2 {
            font-size: 26px !important;
          }

          /* ── FAQ ── */
          .cohort-faq-outer {
            padding: 56px 20px !important;
          }
          .cohort-faq-outer h2 {
            font-size: 28px !important;
          }

          /* ── Generic section headings ── */
          .cohort-section-h2-dark,
          .cohort-section-h2-light {
            font-size: 26px !important;
            line-height: 1.2 !important;
          }
        }
      `}</style>
    </div>
  );
}